import { FormPageLayout, HasIdAndName, HbLoadingSpinner } from "@homebound/beam";
import { useCallback } from "react";
import { Navigate, matchPath, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { createMaterialCatalogUrl, createMaterialDetailsUrl } from "src/RouteUrls";
import { useNavigationCheck } from "src/components/navigation/NavigationContext";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import { NotFound } from "src/routes/NotFound";
import {
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialBrandFragment,
  useMaterialCatalogMetadataQuery,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import type { MaterialListing_DetailFragment } from "src/routes/libraries/material-catalog/components/materialListingDetail.generated";
import { useMaterialListingIdForVariantQuery } from "src/routes/libraries/material-catalog/material-listing-page/MaterialListingPage.generated";
import { AdditionalDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/AdditionalDetailsSection";
import { MaterialDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/MaterialDetailsSection";
import { MaterialListingHistoryPane } from "src/routes/libraries/material-catalog/material-listing-page/components/MaterialListingHistoryPane";
import { VariantDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/VariantDetailsSection";
import {
  MaterialListingByListingIdProvider,
  useMaterialListingContext,
} from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";
import { materialDetailsRoute } from "src/routing/domains/libraries/material-catalog/materialCatalogPaths";
import { queryResult } from "src/utils";

/**
 * The material listing detail page: one MaterialListing, with its variants selectable from a thumbnail
 * strip and the selected one addressable in the URL (`?mv=`), or create mode via `?createNew=1`.
 *
 * Old bookmarks `/libraries/material-catalog/mv:…` redirect to the listing URL with that variant selected.
 */
export function MaterialListingPage() {
  const { listingId } = useParams<{ listingId: string }>();

  if (listingId?.startsWith("mv:")) return <RedirectMaterialVariantToListing variantId={listingId} />;
  if (!listingId?.startsWith("ml:")) return <NotFound />;

  return <MaterialListingPageProviders listingId={listingId} />;
}

type RedirectMaterialVariantToListingProps = { variantId: string };

/** Compat: old `/libraries/material-catalog/mv:X` bookmarks → `/libraries/material-catalog/ml:Y?mv=mv:X`. */
function RedirectMaterialVariantToListing(props: RedirectMaterialVariantToListingProps) {
  const { variantId } = props;
  const { data, error } = useMaterialListingIdForVariantQuery({ variables: { variantId } });
  if (error) return <NotFound />;
  if (!data) return <HbLoadingSpinner />;
  return <Navigate to={createMaterialDetailsUrl(data.materialVariant.listing.id, { mv: variantId })} replace />;
}

type MaterialListingPageProvidersProps = { listingId: string };

function MaterialListingPageProviders(props: MaterialListingPageProvidersProps) {
  const { listingId } = props;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createNew = searchParams.get("createNew") === "1";
  const mvParam = searchParams.get("mv") ?? undefined;
  // Prefer createNew when both are present; strip stray mv via replace below.
  const selectedVariantId = createNew ? undefined : mvParam?.startsWith("mv:") ? mvParam : undefined;

  const onSelectVariant = useCallback(
    (id: string | undefined) => {
      if (!id) return;
      navigate(createMaterialDetailsUrl(listingId, { mv: id }), { replace: true });
    },
    [navigate, listingId],
  );

  const onCreateNew = useCallback(() => {
    navigate(createMaterialDetailsUrl(listingId, { createNew: true }), { replace: true });
  }, [navigate, listingId]);

  const onListingLoaded = useCallback(
    (listing: MaterialListing_DetailFragment) => {
      // Malformed: both createNew and mv → prefer createNew and strip mv.
      if (createNew && mvParam) {
        navigate(createMaterialDetailsUrl(listingId, { createNew: true }), { replace: true });
        return;
      }
      // Create mode: never inject a default mv.
      if (createNew) return;

      const defaultMvId = listing.defaultVariant?.id ?? listing.materialVariants.first?.id;
      const mvOnListing = selectedVariantId ? listing.materialVariants.some((v) => v.id === selectedVariantId) : false;

      // Usable mv already on the listing — nothing to canonicalize.
      if (selectedVariantId && mvOnListing) return;

      // Bare listing, unknown mv, or missing default: land on default (or stay if no variants).
      if (defaultMvId) {
        navigate(createMaterialDetailsUrl(listingId, { mv: defaultMvId }), { replace: true });
      }
    },
    [createNew, mvParam, selectedVariantId, listingId, navigate],
  );

  return (
    <MaterialListingByListingIdProvider
      listingId={listingId}
      selectedVariantId={selectedVariantId}
      createNew={createNew}
      onSelectVariant={onSelectVariant}
      onCreateNew={onCreateNew}
      onListingLoaded={onListingLoaded}
    >
      <MaterialListingPageMetadata listingId={listingId} />
    </MaterialListingByListingIdProvider>
  );
}

type MaterialListingPageMetadataProps = { listingId: string };

/** Items/brands/tags/markets drive the selects; loaded alongside the listing rather than per-section. */
function MaterialListingPageMetadata(props: MaterialListingPageMetadataProps) {
  const { listingId } = props;
  const query = useMaterialCatalogMetadataQuery();
  return queryResult(query, ({ items, materialBrands, materialVariantTags, markets }) => (
    <MaterialListingPageView
      listingId={listingId}
      items={items}
      brands={materialBrands}
      tags={materialVariantTags}
      markets={markets}
    />
  ));
}

type MaterialListingPageViewProps = {
  listingId: string;
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  markets: HasIdAndName[];
};

function MaterialListingPageView(props: MaterialListingPageViewProps) {
  const { listingId, items, brands, tags, markets } = props;
  const { listing, formState, loading, save } = useMaterialListingContext();
  const { useRegisterNavigationCheck } = useNavigationCheck();

  useRegisterNavigationCheck(
    (_current, next) => {
      if (!formState.dirty) return true;
      // No `next` is the browser-level unload (reload/close), which always deserves the prompt.
      if (!next) return false;
      // Same listing path with any search change (thumbnail / createNew) is allowed; leaving prompts.
      const match = matchPath(materialDetailsRoute.path, next.pathname);
      return match?.params.listingId === listingId;
    },
    [formState, listingId],
  );

  if (loading && !listing) return <HbLoadingSpinner />;
  if (!listing) return <NotFound />;

  return (
    // TODO: Navigate this component over to use the newer `FocusedFormPageLayout` component. Once it supports all the features we need.
    // 1. Right side bar for the history pane: https://github.com/homebound-team/beam/pull/1402
    // 2. Allow dirty navigation without useBlocker triggering: https://linear.app/homebound-team/issue/DS-327/allow-focusedformlayout-to-permit-selected-in-app-navigations-while
    <FormPageLayout
      formState={formState}
      pageTitle={listing.name ?? ""}
      breadCrumb={[{ label: "Materials Catalog", href: createMaterialCatalogUrl() }]}
      cancelAction={{ label: "Cancel", onClick: createMaterialCatalogUrl() }}
      submitAction={{ label: "Save", onClick: save }}
      rightSideBar={[{ icon: "history", render: () => <MaterialListingHistoryPane materialListingId={listingId} /> }]}
      formSections={[
        {
          title: "Material Details",
          rows: [{ reactNodeMaterialDetails: <MaterialDetailsSection items={items} brands={brands} /> }],
        },
        {
          title: "Variant Details",
          rows: [{ reactNodeVariantDetails: <VariantDetailsSection items={items} tags={tags} /> }],
        },
        {
          title: "Additional Details",
          rows: [{ reactNodeAdditionalDetails: <AdditionalDetailsSection markets={markets} /> }],
        },
      ]}
    />
  );
}
